"use client";

import React, { useState, useEffect, useRef } from "react";
import {
  Cpu,
  Zap,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Terminal,
  Mic,
  MicOff,
  Volume2,
  Shield,
  Lock,
  Copy,
  Check,
  Trash2,
} from "lucide-react";
import { TileCodeBlock } from "@/components/TileContent";

interface CapabilityCheck {
  id: string;
  name: string;
  status: "checking" | "supported" | "unsupported" | "flag_required";
  detail: string;
  recommendation?: string;
}

export default function WebnnPlayground() {
  const [isAuditing, setIsAuditing] = useState(false);
  const [activeCodeTab, setActiveCodeTab] = useState<"detect" | "transformers" | "onnx">("detect");

  // Audio / Mic Live Stream State & Permission
  const [micPermission, setMicPermission] = useState<"checking" | "granted" | "prompt" | "denied">("checking");
  const [isAudioRunning, setIsAudioRunning] = useState(false);
  const [audioVolume, setAudioVolume] = useState<number[]>(new Array(8).fill(8));
  const [sampleRate, setSampleRate] = useState<number>(48000);
  const [transcribedText, setTranscribedText] = useState<string>("");
  const [copiedTranscript, setCopiedTranscript] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const recognitionRef = useRef<any>(null);
  const isAudioRunningRef = useRef(false);
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll transcript container as new words arrive so text never stays clipped
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [transcribedText]);

  const [checks, setChecks] = useState<CapabilityCheck[]>([
    {
      id: "webnn",
      name: "W3C WebNN API (navigator.ml)",
      status: "checking",
      detail: "Prüfe 'ml' Eigenschaft auf navigator...",
    },
    {
      id: "webgpu",
      name: "WebGPU Schnittstelle (navigator.gpu)",
      status: "checking",
      detail: "Prüfe GPU-Adapter und WGSL-Compute...",
    },
    {
      id: "wasm-simd",
      name: "WebAssembly SIMD (Vektorbefehle)",
      status: "checking",
      detail: "Prüfe 128-Bit SIMD CPU-Beschleunigung...",
    },
    {
      id: "worker",
      name: "Web Worker & Offscreen Support",
      status: "checking",
      detail: "Prüfe Multithreading-Fähigkeit für ruckelfreie UI...",
    },
  ]);

  const runAudit = async () => {
    setIsAuditing(true);

    // 1. WebNN Check
    const hasNavigatorMl = typeof navigator !== "undefined" && "ml" in navigator;
    let webnnStatus: CapabilityCheck["status"] = "unsupported";
    let webnnDetail = "Nicht im aktuellen Browser aktiv. Flag oder Chrome/Edge Canary erforderlich.";
    let webnnRec: string | undefined =
      "Aktiviere 'chrome://flags/#enable-web-machine-learning-neural-network' in Chrome oder Edge.";

    if (hasNavigatorMl) {
      try {
        const ml = (navigator as unknown as { ml?: { createContext?: () => Promise<unknown> } }).ml;
        if (typeof ml?.createContext === "function") {
          webnnStatus = "supported";
          webnnDetail = "Hardware-Beschleuniger (NPU/GPU/CPU) über navigator.ml verfügbar.";
          webnnRec = "Optimal: Lokale Inferenz läuft nativ über DirectML/CoreML.";
        } else {
          webnnStatus = "flag_required";
          webnnDetail = "navigator.ml existiert, erfordert aber experimentelles Origin-Trial oder Flag.";
        }
      } catch {
        webnnStatus = "flag_required";
        webnnDetail = "WebNN Schnittstelle registriert, aber Kontext-Erstellung gesperrt.";
      }
    }

    // 2. WebGPU Check
    let webgpuStatus: CapabilityCheck["status"] = "unsupported";
    let webgpuDetail = "WebGPU nicht verfügbar.";
    let webgpuRec: string | undefined;

    if (typeof navigator !== "undefined" && "gpu" in navigator) {
      try {
        const adapter = await (navigator as unknown as { gpu: { requestAdapter: () => Promise<unknown> } }).gpu.requestAdapter();
        if (adapter) {
          webgpuStatus = "supported";
          webgpuDetail = "WebGPU Compute-Shader einsatzbereit. Exzellenter High-Speed-Fallback.";
        } else {
          webgpuStatus = "unsupported";
          webgpuDetail = "Kein kompatibler Grafiktreiber / GPU-Adapter gefunden.";
        }
      } catch {
        webgpuStatus = "unsupported";
        webgpuDetail = "WebGPU-Adapter konnte nicht initialisiert werden.";
      }
    }

    // 3. WebAssembly SIMD Check
    let simdStatus: CapabilityCheck["status"] = "unsupported";
    let simdDetail = "WASM SIMD nicht verfügbar (Standard-WASM CPU aktiv).";

    try {
      // Minimaler gültiger WASM-Bytecode mit i32x4.splat Vektorbefehl
      const simdBytes = new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10,
        10, 1, 8, 0, 65, 0, 253, 15, 26, 11,
      ]);
      const isSimdSupported = WebAssembly.validate(simdBytes);
      if (isSimdSupported) {
        simdStatus = "supported";
        simdDetail = "128-Bit SIMD Vektorbefehle aktiv. Solider universeller Fallback.";
      }
    } catch {
      simdStatus = "unsupported";
      simdDetail = "SIMD-Validierung fehlgeschlagen.";
    }

    // 4. Web Worker Check
    const hasWorker = typeof Worker !== "undefined";
    const workerStatus: CapabilityCheck["status"] = hasWorker ? "supported" : "unsupported";
    const workerDetail = hasWorker
      ? "Web Worker verfügbar. Rechenintensive Inferenz blockiert nicht den Main Thread."
      : "Keine Worker-Unterstützung gefunden.";

    setChecks([
      {
        id: "webnn",
        name: "W3C WebNN API (navigator.ml)",
        status: webnnStatus,
        detail: webnnDetail,
        recommendation: webnnRec,
      },
      {
        id: "webgpu",
        name: "WebGPU Schnittstelle (navigator.gpu)",
        status: webgpuStatus,
        detail: webgpuDetail,
        recommendation: webgpuRec,
      },
      {
        id: "wasm-simd",
        name: "WebAssembly SIMD (Vektorbefehle)",
        status: simdStatus,
        detail: simdDetail,
      },
      {
        id: "worker",
        name: "Web Worker Multithreading",
        status: workerStatus,
        detail: workerDetail,
      },
    ]);

    setIsAuditing(false);
  };

  useEffect(() => {
    runAudit();
  }, []);

  // Check microphone permissions on mount and listen to changes
  useEffect(() => {
    let permStatus: PermissionStatus | null = null;

    const checkPermission = async () => {
      if (typeof navigator === "undefined" || !navigator.permissions?.query) {
        setMicPermission("prompt");
        return;
      }
      try {
        permStatus = await navigator.permissions.query({ name: "microphone" as any });
        setMicPermission(permStatus.state as "granted" | "denied" | "prompt");
        permStatus.onchange = () => {
          if (permStatus) {
            setMicPermission(permStatus.state as "granted" | "denied" | "prompt");
            if (permStatus.state === "denied") {
              stopAudioTest();
            }
          }
        };
      } catch {
        // Fallback for browsers without 'microphone' query support
        setMicPermission("prompt");
      }
    };

    checkPermission();

    return () => {
      if (permStatus) {
        permStatus.onchange = null;
      }
    };
  }, []);

  // Stop audio on unmount
  useEffect(() => {
    return () => {
      stopAudioTest();
    };
  }, []);

  const startAudioTest = async () => {
    setAudioError(null);

    if (micPermission === "denied") {
      setAudioError(
        "Mikrofon-Zugriff ist im Browser blockiert. Bitte erlaube das Mikrofon in den Website-Einstellungen links neben der URL."
      );
      return;
    }

    try {
      // Requests mic permission from the browser if state was 'prompt'
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;
      setMicPermission("granted");

      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioContextRef.current = audioCtx;
      setSampleRate(audioCtx.sampleRate);

      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 64;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateMeter = () => {
        analyser.getByteFrequencyData(dataArray);
        // Extract 8 representative bars
        const bars = [];
        for (let i = 0; i < 8; i++) {
          const val = dataArray[i * 2] || 0;
          bars.push(Math.max(12, Math.round((val / 255) * 100)));
        }
        setAudioVolume(bars);
        animationFrameRef.current = requestAnimationFrame(updateMeter);
      };

      updateMeter();
      setIsAudioRunning(true);
      isAudioRunningRef.current = true;

      // Optional native speech recognition if supported in browser
      const SpeechRecognition =
        (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "de-DE";

        recognition.onresult = (event: any) => {
          let full = "";
          for (let i = 0; i < event.results.length; ++i) {
            const piece = event.results[i][0]?.transcript || "";
            if (piece) {
              if (full && !full.endsWith(" ") && !piece.startsWith(" ")) {
                full += " ";
              }
              full += piece;
            }
          }
          if (full) {
            setTranscribedText(full.trim());
          }
        };

        recognition.onerror = () => {
          // ignore minor speech errors
        };

        recognition.onend = () => {
          // In Chromium browsers, SpeechRecognition stops after pauses.
          // Restart recognition if user still has the audio test running.
          if (isAudioRunningRef.current && recognitionRef.current) {
            try {
              recognitionRef.current.start();
            } catch {
              // already running or aborted
            }
          }
        };

        try {
          recognition.start();
          recognitionRef.current = recognition;
        } catch {
          // already started or unavailable
        }
      }
    } catch (err: any) {
      isAudioRunningRef.current = false;
      if (err.name === "SecurityError") {
        setMicPermission("denied");
        setAudioError(
          "Zugriff durch Browser-Sicherheitsrichtlinie blockiert (Permissions-Policy). Nach dem Neuladen der Seite ist der Zugriff aktiv."
        );
      } else if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setMicPermission("denied");
        setAudioError(
          "Zugriff verweigert: Klicke links neben der Adresszeile (vor der URL) auf das Website-Symbol, um das Mikrofon zu aktivieren."
        );
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        setAudioError("Kein Mikrofon-Eingabegerät am System gefunden.");
      } else {
        setAudioError(err.message || "Mikrofon-Initialisierung fehlgeschlagen.");
      }
      setIsAudioRunning(false);
    }
  };

  const stopAudioTest = () => {
    isAudioRunningRef.current = false;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
      recognitionRef.current = null;
    }
    setIsAudioRunning(false);
    setAudioVolume(new Array(8).fill(8));
  };

  const handleCopyTranscript = async () => {
    if (!transcribedText) return;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(transcribedText);
      } else {
        throw new Error("Clipboard API unavailable");
      }
      setCopiedTranscript(true);
      setTimeout(() => setCopiedTranscript(false), 2000);
    } catch {
      // Robust textarea fallback
      try {
        const textarea = document.createElement("textarea");
        textarea.value = transcribedText;
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopiedTranscript(true);
        setTimeout(() => setCopiedTranscript(false), 2000);
      } catch {
        // Fallback failed
      }
    }
  };

  const handleClearTranscript = () => {
    setTranscribedText("");
  };

  const supportedCount = checks.filter((c) => c.status === "supported").length;

  return (
    <div className="flex flex-col gap-6 min-h-[460px]">
      {/* Cockpit Header with Real Audit Summary */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-neutral-200/80 pb-4 dark:border-neutral-800/80">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-heading text-base sm:text-lg font-bold text-neutral-900 dark:text-white">
              Dein Live-Browser-Diagnosebericht
            </span>
            <span className="rounded-full border border-purple-500/30 bg-purple-500/10 px-2 py-0.5 font-mono text-[11px] font-bold text-purple-700 dark:text-purple-300">
              {supportedCount} von 4 aktiv
            </span>
          </div>
          <p className="text-xs text-neutral-600 dark:text-neutral-400">
            Prüft live in diesem Tab, welche Hardware-Beschleuniger für In-Browser Machine Learning einsatzbereit sind.
          </p>
        </div>

        <button
          type="button"
          onClick={runAudit}
          disabled={isAuditing}
          className="min-h-[44px] inline-flex items-center gap-2 rounded-xl border border-neutral-300 bg-white px-4 py-2 font-mono text-xs font-semibold text-neutral-800 shadow-xs hover:bg-neutral-50 active:scale-95 disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-750 transition-all cursor-pointer"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${isAuditing ? "animate-spin text-purple-500" : ""}`} />
          <span>Neu prüfen</span>
        </button>
      </div>

      {/* 4 Diagnostic Result Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {checks.map((check) => {
          const isOk = check.status === "supported";
          const isFlag = check.status === "flag_required";

          return (
            <div
              key={check.id}
              className={`flex flex-col justify-between rounded-xl border p-4 transition-all ${
                isOk
                  ? "border-emerald-500/30 bg-emerald-50/40 dark:border-emerald-500/25 dark:bg-emerald-950/20"
                  : isFlag
                  ? "border-amber-500/30 bg-amber-50/40 dark:border-amber-500/25 dark:bg-amber-950/20"
                  : "border-neutral-200 bg-neutral-50/50 dark:border-neutral-800 dark:bg-neutral-900/40"
              }`}
            >
              <div>
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-heading text-sm font-bold text-neutral-900 dark:text-white">
                    {check.name}
                  </span>
                  {isOk ? (
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="h-4 w-4" /> BEREIT
                    </span>
                  ) : isFlag ? (
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] font-bold text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4" /> FLAG NÖTIG
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 font-mono text-[10px] font-bold text-neutral-400 dark:text-neutral-500">
                      <XCircle className="h-4 w-4" /> INAKTIV
                    </span>
                  )}
                </div>

                <p className="text-xs leading-relaxed text-neutral-600 dark:text-neutral-400">
                  {check.detail}
                </p>
              </div>

              {check.recommendation && (
                <div className="mt-3 pt-2.5 border-t border-black/5 dark:border-white/5 font-mono text-[11px] text-purple-700 dark:text-purple-300">
                  💡 {check.recommendation}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Interactive Live Audio / Mic Ingestion Pipeline */}
      <div className="rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-50/40 via-white to-purple-50/20 p-4 sm:p-5 dark:border-purple-500/25 dark:from-purple-950/20 dark:via-neutral-900 dark:to-neutral-950">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-purple-200/60 pb-3 mb-4 dark:border-purple-900/40">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Volume2 className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <h4 className="font-heading font-bold text-sm sm:text-base text-neutral-900 dark:text-white">
                Live In-Browser Audio-Pipeline (Mikrofon-Test)
              </h4>
              {micPermission === "granted" ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-2 py-0.5 font-mono text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
                  <CheckCircle2 className="h-3 w-3" /> Freigegeben
                </span>
              ) : micPermission === "prompt" ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-purple-500/30 bg-purple-500/10 px-2 py-0.5 font-mono text-[10px] font-bold text-purple-700 dark:text-purple-300">
                  <Shield className="h-3 w-3" /> Zugriff erforderlich
                </span>
              ) : micPermission === "denied" ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-rose-500/30 bg-rose-500/10 px-2 py-0.5 font-mono text-[10px] font-bold text-rose-700 dark:text-rose-300">
                  <Lock className="h-3 w-3" /> Im Browser blockiert
                </span>
              ) : null}
            </div>
            <p className="text-xs text-neutral-600 dark:text-neutral-400 mt-1">
              Erlebe, wie der Browser Audio lokal als PCM-Puffer abtastet – die Grundlage für Speech-to-Text (Whisper).
            </p>
          </div>

          <button
            type="button"
            onClick={isAudioRunning ? stopAudioTest : startAudioTest}
            className={`min-h-[44px] inline-flex items-center gap-2 rounded-xl px-4 py-2 font-mono text-xs font-semibold transition-all cursor-pointer shadow-xs ${
              isAudioRunning
                ? "bg-rose-600 text-white hover:bg-rose-700 active:scale-95"
                : micPermission === "denied"
                ? "bg-neutral-800 text-white hover:bg-neutral-700 active:scale-95"
                : "bg-purple-600 text-white hover:bg-purple-700 active:scale-95"
            }`}
          >
            {isAudioRunning ? (
              <>
                <MicOff className="h-4 w-4" />
                <span>Test beenden</span>
              </>
            ) : micPermission === "denied" ? (
              <>
                <Lock className="h-4 w-4 text-rose-400" />
                <span>Zugriff im Browser freigeben</span>
              </>
            ) : micPermission === "granted" ? (
              <>
                <Mic className="h-4 w-4" />
                <span>Audio-Stream starten</span>
              </>
            ) : (
              <>
                <Mic className="h-4 w-4" />
                <span>Zugriff anfragen & testen</span>
              </>
            )}
          </button>
        </div>

        {/* Guided unlock instructions if microphone permission was denied */}
        {micPermission === "denied" && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-50/70 p-3.5 text-xs text-rose-900 dark:bg-rose-950/30 dark:text-rose-200 mb-3 space-y-1.5">
            <div className="flex items-center gap-2 font-bold">
              <Lock className="h-4 w-4 text-rose-500 shrink-0" />
              <span>Mikrofon-Zugriff im Browser freischalten:</span>
            </div>
            <ol className="list-decimal list-inside leading-relaxed text-rose-800 dark:text-rose-300 space-y-0.5 pl-1">
              <li>Klicke links in der Browser-Adresszeile (vor der URL) auf das <strong>Website-Icon (Schloss / Schieberegler)</strong>.</li>
              <li>Aktiviere den Schalter <strong>Mikrofon</strong> auf <strong>Zulassen</strong>.</li>
              <li>Klicke anschließend erneut auf den Button, um den Live-Test zu starten.</li>
            </ol>
          </div>
        )}

        {audioError && micPermission !== "denied" && (
          <div className="rounded-xl border border-rose-500/30 bg-rose-50/60 p-3 text-xs text-rose-700 dark:bg-rose-950/30 dark:text-rose-300 mb-3">
            ⚠️ {audioError}
          </div>
        )}

        {/* Audio Visualizer & Telemetry */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-center">
          {/* Real-time Spectrum Bars */}
          <div className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900/80 sm:col-span-1 flex flex-col justify-between h-24">
            <span className="font-mono text-[10px] text-neutral-500 font-bold uppercase tracking-wider">
              {isAudioRunning ? "Echtzeit-Pegel (Analyser)" : "Audio-Pegel (Inaktiv)"}
            </span>
            <div className="flex items-end justify-between gap-1.5 h-12 px-1">
              {audioVolume.map((h, i) => (
                <div
                  key={i}
                  className="w-full rounded-t-sm transition-all duration-75"
                  style={{
                    height: `${isAudioRunning ? h : 10}%`,
                    backgroundColor: isAudioRunning
                      ? h > 65
                        ? "#a855f7"
                        : "#8b5cf6"
                      : "rgba(168,85,247,0.2)",
                  }}
                />
              ))}
            </div>
          </div>

          {/* Telemetry Chips */}
          <div className="rounded-xl border border-neutral-200 bg-white p-3 dark:border-neutral-800 dark:bg-neutral-900/80 sm:col-span-2 flex flex-col justify-between h-24">
            <div className="flex items-center justify-between font-mono text-[10px] text-neutral-500 font-bold uppercase tracking-wider">
              <span>On-Device Audio-Telemetrie</span>
              <span className={isAudioRunning ? "text-emerald-600 dark:text-emerald-400" : "text-neutral-400"}>
                {isAudioRunning ? "● STREAM LÄUFT" : "○ BEREIT"}
              </span>
            </div>

            <div className="grid grid-cols-3 gap-2 font-mono text-xs">
              <div>
                <span className="text-[10px] text-neutral-400 block">Sample-Rate</span>
                <span className="font-bold text-neutral-900 dark:text-white">
                  {isAudioRunning ? `${sampleRate.toLocaleString()} Hz` : "48.000 Hz"}
                </span>
              </div>
              <div>
                <span className="text-[10px] text-neutral-400 block">Puffer-Format</span>
                <span className="font-bold text-neutral-900 dark:text-white">Float32 PCM</span>
              </div>
              <div>
                <span className="text-[10px] text-neutral-400 block">Latenz</span>
                <span className="font-bold text-emerald-600 dark:text-emerald-400">&lt; 8 ms</span>
              </div>
            </div>

            <div className="mt-1 flex items-center justify-between text-[11px] font-mono text-neutral-500">
              <span>Spracherkennung:</span>
              <span
                className={`font-semibold ${
                  isAudioRunning
                    ? "text-purple-600 dark:text-purple-400"
                    : transcribedText
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-neutral-400"
                }`}
              >
                {isAudioRunning
                  ? "● Hört aktiv zu..."
                  : transcribedText
                  ? "✓ Text erfasst"
                  : "○ Bereit für Sprache"}
              </span>
            </div>
          </div>
        </div>

        {/* Dedicated Transcribed Text Box with Copy & Reset */}
        {(transcribedText || isAudioRunning) && (
          <div className="mt-4 rounded-xl border border-purple-500/25 bg-white/95 p-3.5 shadow-2xs dark:border-purple-500/20 dark:bg-neutral-900/95 transition-all">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 pb-2 mb-2.5 dark:border-neutral-800">
              <div className="flex items-center gap-2">
                <span className="font-heading text-xs sm:text-sm font-bold text-neutral-900 dark:text-white flex items-center gap-1.5">
                  🗣️ Live-Sprachtranskription
                </span>
                {isAudioRunning && (
                  <span className="inline-flex items-center gap-1 rounded-full border border-purple-500/30 bg-purple-500/10 px-2 py-0.5 font-mono text-[10px] font-bold text-purple-600 dark:text-purple-400">
                    <span className="h-1.5 w-1.5 rounded-full bg-purple-500 animate-pulse" />
                    LIVE
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {transcribedText && (
                  <>
                    <span className="font-mono text-[10px] text-neutral-400">
                      {transcribedText.split(/\s+/).filter(Boolean).length} Wörter • {transcribedText.length} Zeichen
                    </span>
                    <button
                      type="button"
                      onClick={handleCopyTranscript}
                      className={`min-h-[32px] inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 font-mono text-xs font-semibold transition-all cursor-pointer ${
                        copiedTranscript
                          ? "bg-emerald-500 text-white shadow-xs"
                          : "bg-purple-100 hover:bg-purple-200 text-purple-800 dark:bg-purple-950/60 dark:text-purple-300 dark:hover:bg-purple-900/80"
                      }`}
                      title="Transkript in Zwischenablage kopieren"
                    >
                      {copiedTranscript ? (
                        <>
                          <Check className="h-3.5 w-3.5" />
                          <span>Kopiert!</span>
                        </>
                      ) : (
                        <>
                          <Copy className="h-3.5 w-3.5 opacity-80" />
                          <span>Kopieren</span>
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={handleClearTranscript}
                      className="min-h-[32px] inline-flex items-center gap-1 rounded-lg px-2 py-1 font-mono text-xs text-neutral-400 hover:text-neutral-700 hover:bg-neutral-100 dark:hover:bg-neutral-800 dark:hover:text-neutral-200 transition-colors cursor-pointer"
                      title="Transkript zurücksetzen"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
              </div>
            </div>

            <div
              ref={transcriptContainerRef}
              className="min-h-[52px] max-h-60 overflow-y-auto pr-2 rounded-lg bg-neutral-50/70 p-3 dark:bg-neutral-950/40 border border-neutral-200/60 dark:border-neutral-800/60"
            >
              {transcribedText ? (
                <p className="font-sans text-xs sm:text-sm leading-relaxed text-neutral-800 dark:text-neutral-200 break-words whitespace-pre-wrap select-text">
                  {transcribedText}
                </p>
              ) : (
                <p className="font-sans text-xs italic text-neutral-400 dark:text-neutral-500">
                  Sprich jetzt ins Mikrofon – deine gesprochenen Sätze werden hier live erfasst, umgebrochen und können mit einem Klick kopiert werden...
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Code Snippets with Proper Syntax Highlighting */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <div className="flex items-center gap-2 font-mono text-xs font-bold text-neutral-700 dark:text-neutral-300">
            <Terminal className="h-4 w-4 text-purple-500" />
            <span>Praxis-Code: So bindest du die geprüfte Kaskade ein</span>
          </div>

          <div className="flex rounded-lg bg-neutral-100 p-0.5 border border-neutral-200 dark:bg-neutral-850 dark:border-neutral-700">
            <button
              type="button"
              onClick={() => setActiveCodeTab("detect")}
              className={`min-h-[32px] px-2.5 py-1 text-xs rounded-md font-mono transition-colors cursor-pointer ${
                activeCodeTab === "detect"
                  ? "bg-white dark:bg-neutral-900 text-purple-600 dark:text-purple-400 font-bold shadow-xs"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900"
              }`}
            >
              1. Feature Detection
            </button>
            <button
              type="button"
              onClick={() => setActiveCodeTab("transformers")}
              className={`min-h-[32px] px-2.5 py-1 text-xs rounded-md font-mono transition-colors cursor-pointer ${
                activeCodeTab === "transformers"
                  ? "bg-white dark:bg-neutral-900 text-purple-600 dark:text-purple-400 font-bold shadow-xs"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900"
              }`}
            >
              2. Transformers.js
            </button>
            <button
              type="button"
              onClick={() => setActiveCodeTab("onnx")}
              className={`min-h-[32px] px-2.5 py-1 text-xs rounded-md font-mono transition-colors cursor-pointer ${
                activeCodeTab === "onnx"
                  ? "bg-white dark:bg-neutral-900 text-purple-600 dark:text-purple-400 font-bold shadow-xs"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900"
              }`}
            >
              3. ONNX Runtime Web
            </button>
          </div>
        </div>

        {activeCodeTab === "detect" && (
          <TileCodeBlock
            title="webnn-feature-detection.ts"
            language="typescript"
            showLineNumbers
          >
{`// Robuste Feature Detection für WebNN und WebGPU
export async function getBestAvailableBackend(): Promise<'webnn' | 'webgpu' | 'wasm'> {
  // 1. Prüfe W3C WebNN (NPU-Beschleunigung)
  if (typeof navigator !== 'undefined' && 'ml' in navigator) {
    try {
      const ml = (navigator as any).ml;
      const context = await ml.createContext({ deviceType: 'npu' });
      if (context) return 'webnn';
    } catch {
      // NPU nicht verfügbar, versuche nächsten Provider
    }
  }

  // 2. Fallback auf WebGPU (Grafikkarte)
  if (typeof navigator !== 'undefined' && 'gpu' in navigator) {
    const adapter = await navigator.gpu.requestAdapter();
    if (adapter) return 'webgpu';
  }

  // 3. Universeller Fallback: WebAssembly auf der CPU
  return 'wasm';
}`}
          </TileCodeBlock>
        )}

        {activeCodeTab === "transformers" && (
          <TileCodeBlock
            title="transformers-pipeline.ts"
            language="typescript"
            showLineNumbers
          >
{`import { pipeline } from '@huggingface/transformers';

// Initialisiert Pipeline mit Hardware-Beschleunigung & 8-Bit-Quantisierung
const segmenter = await pipeline(
  'image-segmentation',
  'Xenova/segformer-b0-finetuned-ade-512-512',
  {
    device: 'webgpu', // Nutzt WebGPU / WebNN in ORT Web
    dtype: 'q8',      // Spart 75% RAM & Ladezeit
  }
);

// Führt Inferenz direkt auf dem HTMLVideoElement aus (< 15ms)
const mask = await segmenter(videoElement);`}
          </TileCodeBlock>
        )}

        {activeCodeTab === "onnx" && (
          <TileCodeBlock
            title="onnx-webnn-session.ts"
            language="typescript"
            showLineNumbers
          >
{`import * as ort from 'onnxruntime-web/experimental';

// Direkte Konfiguration der Hardware-Kaskade in ONNX Runtime Web
const session = await ort.InferenceSession.create('./model.onnx', {
  executionProviders: [
    {
      name: 'webnn',
      deviceType: 'npu',            // Bevorzuge lüfterlose NPU
      powerPreference: 'low-power', // Schont Laptop-Akkus
    },
    'webgpu',                       // Fallback 1: Grafikkarte
    'wasm',                         // Fallback 2: CPU SIMD
  ],
});`}
          </TileCodeBlock>
        )}
      </div>
    </div>
  );
}
